import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CalendarRange, Dumbbell, Minus, Plus, Receipt, Search, Users as UsersIcon, ChevronRight, AlertTriangle } from 'lucide-react';
import type { MemberSummary, MembershipPlan, PackageKind, Payment, PaymentMethod } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select, StatusBadge, Avatar, Skeleton, EmptyState, Modal, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { SubscriptionLockNotice, useSubscriptionLocked } from '../components/SubscriptionLockNotice';
import { useGymData } from '../data/GymDataProvider';
import { listMembers, recordPayment } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { computePeriod, statusToBadge } from '../lib/membership';
import { money, formatDate, planDuration, todayIso } from '../lib/format';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ECOCASH', label: 'EcoCash' },
  { value: 'ONEMONEY', label: 'OneMoney' },
  { value: 'INNBUCKS', label: 'InnBucks' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'OTHER', label: 'Other' },
];

function packageKindLabel(kind?: PackageKind | null) {
  if (kind === 'CLASS') return 'Class';
  if (kind === 'OTHER') return 'Other';
  return 'Personal training';
}

/** What was actually recorded — one transaction can produce a membership payment,
 * a personal-training payment, or both. */
interface SaleResult {
  membership?: Payment;
  pt?: Payment;
  /** Set when membership saved but the PT line failed — so the owner can retry PT. */
  partialError?: string;
}

export function RecordPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselected = (location.state as { memberId?: string } | null)?.memberId;

  const { plans, coaches } = useGymData();
  const locked = useSubscriptionLocked();
  const activePlans = plans.filter((p) => p.active);
  const membershipPlans = activePlans.filter((p) => p.type !== 'PERSONAL_TRAINING');
  const ptPlans = activePlans.filter((p) => p.type === 'PERSONAL_TRAINING');
  const sellsMembership = membershipPlans.length > 0;
  const sellsPt = ptPlans.length > 0;
  const { data, loading } = useAsync(() => listMembers({ size: 1000 }));
  const members = useMemo(() => data?.items ?? [], [data]);

  const [step, setStep] = useState<'pick' | 'form'>(preselected ? 'form' : 'pick');
  const [memberId, setMemberId] = useState(preselected ?? '');

  // Gym-access line.
  const [mActive, setMActive] = useState(sellsMembership);
  const [mPlanId, setMPlanId] = useState(membershipPlans[0]?.id ?? '');
  const [mAmount, setMAmount] = useState(membershipPlans[0]?.price ?? '');
  /** How many plan periods this payment covers — "3 months up front" is one payment, not three. */
  const [mPeriods, setMPeriods] = useState(1);

  // Package line.
  const [ptActive, setPtActive] = useState(!sellsMembership && sellsPt);
  const [ptPlanId, setPtPlanId] = useState(ptPlans[0]?.id ?? '');
  const [ptAmount, setPtAmount] = useState(ptPlans[0]?.price ?? '');
  const [coachId, setCoachId] = useState('');

  // Shared across both lines.
  const [method, setMethod] = useState<PaymentMethod>('ECOCASH');
  const [paidOn, setPaidOn] = useState(todayIso);
  const [note, setNote] = useState('');

  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SaleResult | null>(null);

  const member = members.find((m) => m.id === memberId);
  const mPlan = membershipPlans.find((p) => p.id === mPlanId);
  const ptPlan = ptPlans.find((p) => p.id === ptPlanId);

  // Default the PT coach to the member's assigned trainer (still changeable).
  useEffect(() => {
    if (ptActive && !coachId && member?.coachId) setCoachId(member.coachId);
  }, [ptActive, member?.coachId]); // eslint-disable-line react-hooks/exhaustive-deps

  const coverage = useMemo(
    () =>
      mPlan
        ? computePeriod({
            paidOn,
            currentExpiry: member?.currentExpiry ?? null,
            plan: { durationUnit: mPlan.durationUnit, durationCount: mPlan.durationCount * mPeriods },
          })
        : null,
    [paidOn, member?.currentExpiry, mPlan, mPeriods],
  );
  const isStacking = member?.status === 'ACTIVE' || member?.status === 'EXPIRING';
  const total = (mActive ? Number(mAmount || 0) : 0) + (ptActive ? Number(ptAmount || 0) : 0);
  const coverageLabel = mPlan ? planDuration(mPlan.durationUnit, mPlan.durationCount * mPeriods) : '';

  const coachName = coaches.find((c) => c.id === coachId)?.fullName;
  const methodLabel = METHODS.find((m) => m.value === method)?.label ?? method;

  function priceFor(plan: MembershipPlan, periods: number): string {
    return (Number(plan.price) * periods).toFixed(2);
  }
  function onMPlanChange(id: string) {
    setMPlanId(id);
    const p = membershipPlans.find((x) => x.id === id);
    if (p) setMAmount(priceFor(p, mPeriods)); // prefill, still editable
  }
  function onMPeriodsChange(n: number) {
    const next = Math.min(36, Math.max(1, Math.round(n) || 1));
    setMPeriods(next);
    if (mPlan) setMAmount(priceFor(mPlan, next));
  }
  function onPtPlanChange(id: string) {
    setPtPlanId(id);
    const p = ptPlans.find((x) => x.id === id);
    if (p) setPtAmount(p.price);
  }

  function saleError(): string | null {
    if (!member) return 'Choose a member.';
    if (!mActive && !ptActive) return 'Add at least one item to the sale.';
    if (mActive && !mPlan) return 'Pick a gym access plan.';
    if (ptActive && !ptPlan) return 'Pick a package.';
    if (ptActive && ptPlan && ptPlan.coachRequired !== false && !coachId) {
      return 'Pick the coach for this package.';
    }
    return null;
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const msg = saleError();
    if (msg) {
      setError(msg);
      return;
    }
    setError(null);
    setReviewOpen(true);
  }

  async function recordSale() {
    if (!member) return;
    const msg = saleError();
    if (msg) {
      setError(msg);
      setReviewOpen(false);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      let membershipPayment: Payment | undefined;
      let ptPayment: Payment | undefined;

      if (mActive && mPlan) {
        membershipPayment = await recordPayment({
          memberId: member.id,
          planId: mPlan.id,
          amount: mAmount,
          method,
          paidOn,
          note: note || undefined,
          type: 'MEMBERSHIP',
          periods: mPeriods > 1 ? mPeriods : undefined,
        });
      }

      if (ptActive && ptPlan) {
        try {
          ptPayment = await recordPayment({
            memberId: member.id,
            planId: ptPlan.id,
            amount: ptAmount,
            method,
            paidOn,
            note: note || undefined,
            type: 'PERSONAL_TRAINING',
            coachId: coachId || undefined,
          });
        } catch (err) {
          if (membershipPayment) {
            setReviewOpen(false);
            setResult({
              membership: membershipPayment,
              partialError: err instanceof ApiClientError ? err.message : 'The package payment failed. Try adding it again.',
            });
            return;
          }
          throw err;
        }
      }

      setReviewOpen(false);
      setResult({ membership: membershipPayment, pt: ptPayment });
    } catch (err) {
      setReviewOpen(false);
      setError(err instanceof ApiClientError ? err.message : 'Could not record the sale. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // A lapsed subscription puts the gym in read-only mode — no new payments until renewal.
  if (locked) {
    return <SubscriptionLockNotice action="record payments" />;
  }

  if (loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="mt-4 h-96" />
      </section>
    );
  }

  // Step 1 — choose who the payment is for (skipped when arriving with a member).
  if (step === 'pick') {
    return (
      <MemberPicker
        members={members}
        onBack={() => (memberId ? setStep('form') : navigate(-1))}
        onPick={(id) => {
          setMemberId(id);
          setStep('form');
        }}
      />
    );
  }

  if (result && member) {
    return (
      <Success
        member={member}
        result={result}
        onAnother={() => {
          setResult(null);
          setNote('');
          onMPeriodsChange(1);
        }}
      />
    );
  }

  return (
    <section className="animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Back
      </button>

      <PageHeader
        eyebrow="New payment"
        title="Record a payment"
        description="Tick what they paid for. Gym access sets when they can walk in. Packages (PT, classes) sit on top and don’t change that date."
      />

      <form onSubmit={onSubmit} className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        {/* Builder */}
        <Card className="flex flex-col gap-5 p-5 md:p-6">
          {/* Selected member — tap Change to return to the searchable picker. */}
          <div>
            <span className="text-sm font-medium text-ink-soft">Member</span>
            {member ? (
              <div className="mt-1.5 flex items-center gap-3 rounded-control border border-line bg-elevated p-3">
                <Avatar name={member.fullName} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">{member.fullName}</p>
                  <p className="truncate text-sm text-muted tabular">{member.phone}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('pick')}
                  className="shrink-0 rounded-pill px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="mt-1.5">
                <Button type="button" variant="outline" onClick={() => setStep('pick')}>
                  Choose member
                </Button>
              </div>
            )}
          </div>

          <p className="-mb-1 text-sm font-medium text-ink-soft">What did they pay for?</p>

          {/* Gym access */}
          {sellsMembership ? (
            <div>
              <LineHeader
                active={mActive}
                onToggle={() => setMActive((v) => !v)}
                icon={<CalendarRange className="size-4" aria-hidden />}
                title="Gym access"
                subtitle="Sets the walk-in expiry"
              />
              {mActive && (
                <div className="mt-3 grid gap-5 sm:grid-cols-2">
                  <Select label="Plan" value={mPlanId} onChange={(e) => onMPlanChange(e.target.value)} required>
                    {membershipPlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {money(p.price)} / {planDuration(p.durationUnit, p.durationCount)}
                      </option>
                    ))}
                  </Select>
                  <PeriodStepper
                    plan={mPlan}
                    value={mPeriods}
                    onChange={onMPeriodsChange}
                  />
                  <Input
                    label="Amount"
                    inputMode="decimal"
                    value={mAmount}
                    onChange={(e) => setMAmount(e.target.value)}
                    leading="$"
                    required
                    hint={
                      mPeriods > 1 && mPlan
                        ? `${mPeriods} × ${money(mPlan.price)} — edit for a discount`
                        : 'Editable for part-payments'
                    }
                  />
                  {mPlan && (
                    <div className="flex items-end">
                      <p className="rounded-control bg-surface px-3.5 py-2.5 text-sm text-ink-soft">
                        Covers <span className="font-semibold text-ink">{coverageLabel}</span>
                        {coverage && (
                          <>
                            {' '}· until <span className="font-semibold tabular text-ink">{formatDate(coverage.periodEnd)}</span>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <MissingCatalog
              title="No gym access plans"
              hint="Add a day pass or monthly plan so you can record walk-in access."
              to="/plans"
              action="Add gym access"
            />
          )}

          {/* Packages */}
          {sellsPt ? (
            <div>
              <LineHeader
                active={ptActive}
                onToggle={() => {
                  setPtActive((v) => {
                    const next = !v;
                    if (next && ptPlans.length === 1) onPtPlanChange(ptPlans[0].id);
                    return next;
                  });
                }}
                icon={<Dumbbell className="size-4" aria-hidden />}
                title="Package"
                subtitle="PT, classes, extras"
              />
              {ptActive && (
                <div className="mt-3 flex flex-col gap-4">
                  <PackagePicker plans={ptPlans} selectedId={ptPlanId} onSelect={onPtPlanChange} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Input
                      label="Amount"
                      inputMode="decimal"
                      value={ptAmount}
                      onChange={(e) => setPtAmount(e.target.value)}
                      leading="$"
                      required
                      hint="Editable for discounts"
                    />
                    <Select
                      label={ptPlan?.coachRequired === false ? 'Coach (optional)' : 'Coach'}
                      value={coachId}
                      onChange={(e) => setCoachId(e.target.value)}
                      required={ptPlan?.coachRequired !== false}
                    >
                      <option value="">Select a coach…</option>
                      {coaches.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.fullName}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <MissingCatalog
              title="No packages yet"
              hint="Add personal training, classes, or other extras. They sell on top of gym access and don’t change the expiry."
              to="/plans?tab=packages"
              action="Add a package"
            />
          )}

          {/* Shared payment details */}
          <div className="grid gap-5 border-t border-line pt-5 sm:grid-cols-2">
            <Select label="Payment method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} required>
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
            <Input label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} required />
          </div>

          <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid via front desk" />
        </Card>

        {/* Live summary */}
        <div className="flex flex-col gap-3">
          <Card tone="dark" className="p-5">
            {member && (
              <div className="flex items-center gap-3">
                <Avatar name={member.fullName} />
                <div className="min-w-0">
                  <p className="truncate font-medium text-on-dark">{member.fullName}</p>
                  <p className="truncate text-sm text-on-dark-muted tabular">{member.phone}</p>
                </div>
                <StatusBadge status={statusToBadge(member.status)} className="ml-auto" />
              </div>
            )}

            <div className="mt-5 space-y-4 border-t border-dark-line pt-5 text-sm">
              {!mActive && !ptActive && (
                <p className="text-on-dark-muted">Tick gym access, a package, or both.</p>
              )}

              {mActive && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex min-w-0 items-center gap-2 font-medium text-on-dark">
                      <CalendarRange className="size-4 shrink-0 text-accent" aria-hidden />
                      <span className="truncate">
                        {mPlan?.name ?? 'Gym access'}
                        {mPeriods > 1 && <span className="text-on-dark-muted"> × {mPeriods}</span>}
                      </span>
                    </span>
                    <span className="shrink-0 font-medium text-on-dark tabular">{money(mAmount || 0)}</span>
                  </div>
                  {coverage && (
                    <p className="text-xs text-on-dark-muted">
                      {isStacking ? 'Renews from current expiry · ' : 'Starts on the payment date · '}
                      {mPeriods > 1 ? `${coverageLabel} · ` : ''}new expiry{' '}
                      <span className="rounded-pill bg-accent px-1.5 py-0.5 font-medium text-accent-ink tabular">{formatDate(coverage.periodEnd)}</span>
                    </p>
                  )}
                </div>
              )}

              {ptActive && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex min-w-0 items-center gap-2 font-medium text-on-dark">
                      <Dumbbell className="size-4 shrink-0 text-accent" aria-hidden />
                      <span className="truncate">{ptPlan?.name ?? 'Package'}</span>
                    </span>
                    <span className="shrink-0 font-medium text-on-dark tabular">{money(ptAmount || 0)}</span>
                  </div>
                  <p className="text-xs text-on-dark-muted">
                    {packageKindLabel(ptPlan?.packageKind)}
                    {coachName ? ` · ${coachName}` : ptPlan?.coachRequired === false ? '' : ' · pick a coach'}
                    {' · doesn’t change gym access'}
                  </p>
                </div>
              )}
            </div>

            {ptActive && !mActive && (
              <p className="mt-4 flex items-start gap-2 rounded-control bg-dark-soft p-3 text-xs text-on-dark">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
                <span>
                  <span className="font-semibold">Package only.</span>{' '}
                  <span className="text-on-dark-muted">This won’t extend gym access — tick Gym access too if they also paid for that.</span>
                </span>
              </p>
            )}
          </Card>

          <Card className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted">Total</p>
              <p className="font-display text-display-md tabular text-ink">{money(total)}</p>
            </div>
            <Button type="submit" size="lg" disabled={!member || (!mActive && !ptActive)}>
              <Check className="size-5" aria-hidden />
              Record payment
            </Button>
          </Card>

          {error && (
            <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">
              {error}
            </p>
          )}
        </div>
      </form>

      <Modal
        open={reviewOpen}
        onClose={() => !submitting && setReviewOpen(false)}
        title="Record this sale?"
        description={member ? `For ${member.fullName}` : undefined}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setReviewOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button size="sm" onClick={recordSale} loading={submitting}>
              {!submitting && <Check className="size-4" aria-hidden />}
              Record {money(total)}
            </Button>
          </div>
        }
      >
        <ul className="divide-y divide-line rounded-control border border-line">
          {mActive && mPlan && (
            <li className="flex items-start justify-between gap-3 px-3.5 py-3">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">Gym access</p>
                <p className="mt-0.5 font-medium text-ink">
                  {mPlan.name}
                  {mPeriods > 1 && <span className="text-muted"> × {mPeriods} · {coverageLabel}</span>}
                </p>
                {coverage && (
                  <p className="mt-0.5 text-sm text-muted">Active until {formatDate(coverage.periodEnd)}</p>
                )}
              </div>
              <p className="shrink-0 font-medium tabular text-ink">{money(mAmount || 0)}</p>
            </li>
          )}
          {ptActive && ptPlan && (
            <li className="flex items-start justify-between gap-3 px-3.5 py-3">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">Package</p>
                <p className="mt-0.5 font-medium text-ink">{ptPlan.name}</p>
                <p className="mt-0.5 text-sm text-muted">
                  {packageKindLabel(ptPlan.packageKind)}
                  {coachName ? ` · ${coachName}` : ''}
                </p>
              </div>
              <p className="shrink-0 font-medium tabular text-ink">{money(ptAmount || 0)}</p>
            </li>
          )}
          <li className="flex items-center justify-between gap-3 bg-surface px-3.5 py-3">
            <p className="font-medium text-ink">Total · {methodLabel} · {formatDate(paidOn)}</p>
            <p className="font-display text-lg font-semibold tabular text-ink">{money(total)}</p>
          </li>
        </ul>
      </Modal>
    </section>
  );
}

/**
 * "How many months?" — one payment covering several plan periods. Label follows the plan
 * (months for monthly plans, periods otherwise) so the front desk never does date maths.
 */
function PeriodStepper({
  plan,
  value,
  onChange,
}: {
  plan: MembershipPlan | undefined;
  value: number;
  onChange: (n: number) => void;
}) {
  const monthly = plan?.durationUnit === 'MONTH' && plan.durationCount === 1;
  const label = monthly ? 'Months paid' : 'Periods paid';
  const quick = monthly ? [1, 2, 3, 6, 12] : [1, 2, 3];
  return (
    <div>
      <span className="text-sm font-medium text-ink-soft">{label}</span>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="inline-flex h-touch items-center rounded-control border border-line bg-elevated">
          <button
            type="button"
            aria-label="Fewer"
            onClick={() => onChange(value - 1)}
            disabled={value <= 1}
            className="grid h-full w-10 place-items-center text-ink transition-colors hover:bg-surface disabled:opacity-40"
          >
            <Minus className="size-4" aria-hidden />
          </button>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={36}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label={label}
            className="h-full w-12 border-x border-line bg-transparent text-center font-medium tabular text-ink outline-none"
          />
          <button
            type="button"
            aria-label="More"
            onClick={() => onChange(value + 1)}
            disabled={value >= 36}
            className="grid h-full w-10 place-items-center text-ink transition-colors hover:bg-surface disabled:opacity-40"
          >
            <Plus className="size-4" aria-hidden />
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {quick.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-pressed={value === n}
              className={cn(
                'rounded-pill px-2.5 py-1 text-xs font-medium transition-colors',
                value === n ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-elevated hover:text-ink',
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-1.5 text-xs text-muted">
        {plan
          ? `Each is ${planDuration(plan.durationUnit, plan.durationCount)} of ${plan.name}. Pay several at once.`
          : 'Pay several periods in one go.'}
      </p>
    </div>
  );
}

/** A tappable line toggle — the whole header flips the item in/out of the sale. */
function LineHeader({
  active,
  onToggle,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onToggle: () => void;
  icon: ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'flex w-full items-center gap-3 rounded-control border px-3.5 py-3 text-left transition-colors',
        active ? 'border-ink bg-elevated' : 'border-line bg-surface hover:bg-elevated',
      )}
    >
      <span
        className={cn(
          'grid size-5 shrink-0 place-items-center rounded-[6px] border transition-colors',
          active ? 'border-ink bg-ink text-on-dark' : 'border-line-strong bg-elevated',
        )}
      >
        {active && <Check className="size-3.5" aria-hidden />}
      </span>
      <span className="inline-flex items-center gap-2 text-ink">
        {icon}
        <span className="font-medium">{title}</span>
      </span>
      <span className="ml-auto text-xs text-muted">{subtitle}</span>
    </button>
  );
}

function PackagePicker({
  plans,
  selectedId,
  onSelect,
}: {
  plans: MembershipPlan[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="grid gap-2">
      {plans.map((p) => {
        const selected = p.id === selectedId;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            aria-pressed={selected}
            className={cn(
              'flex w-full items-center gap-3 rounded-control border px-3.5 py-3 text-left transition-colors',
              selected ? 'border-ink bg-elevated' : 'border-line bg-surface hover:bg-elevated',
            )}
          >
            <span
              className={cn(
                'grid size-5 shrink-0 place-items-center rounded-full border transition-colors',
                selected ? 'border-ink bg-ink text-on-dark' : 'border-line-strong bg-elevated',
              )}
            >
              {selected && <Check className="size-3" aria-hidden />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">{p.name}</span>
              <span className="block text-xs text-muted">
                {packageKindLabel(p.packageKind)} · valid {planDuration(p.durationUnit, p.durationCount)}
              </span>
            </span>
            <span className="shrink-0 font-medium tabular text-ink">{money(p.price)}</span>
          </button>
        );
      })}
    </div>
  );
}

function MissingCatalog({ title, hint, to, action }: { title: string; hint: string; to: string; action: string }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-control border border-dashed border-line px-4 py-4">
      <p className="font-medium text-ink">{title}</p>
      <p className="mt-1 text-sm text-muted">{hint}</p>
      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => navigate(to)}>
        {action}
      </Button>
    </div>
  );
}

/** Step 1: search and tap the member to record a payment for. */
function MemberPicker({
  members,
  onBack,
  onPick,
}: {
  members: MemberSummary[];
  onBack: () => void;
  onPick: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) => m.fullName.toLowerCase().includes(q) || m.phone.includes(q) || (m.whatsappPhone ?? '').includes(q),
    );
  }, [query, members]);

  return (
    <section className="animate-fade-up">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Back
      </button>

      <PageHeader eyebrow="New payment" title="Who’s paying?" description="Search and tap the member you’re recording a payment for." />

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          type="search"
          inputMode="search"
          placeholder="Search by name or phone…"
          aria-label="Search members"
          autoFocus
          className="h-touch w-full rounded-control border border-line bg-elevated pl-10 pr-4 text-ink placeholder:text-muted focus:border-ink-soft"
        />
      </div>

      <Card className="overflow-hidden p-0">
        {filtered.length ? (
          <ul className="divide-y divide-line">
            {filtered.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => onPick(m.id)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface md:px-5"
                >
                  <Avatar name={m.fullName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{m.fullName}</p>
                    <p className="truncate text-sm text-muted tabular">{m.phone}</p>
                  </div>
                  <StatusBadge status={statusToBadge(m.status)} />
                  <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : members.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="No members yet"
            description="Add a member before recording a payment."
            className="rounded-none shadow-none"
            action={<Button onClick={() => navigate('/members/new')}>Add member</Button>}
          />
        ) : (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="No members match"
            description="Try a different name or phone number."
            className="rounded-none shadow-none"
          />
        )}
      </Card>
    </section>
  );
}

function Row({ label, value, dark }: { label: string; value: string; dark?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={dark ? 'text-on-dark-muted' : 'text-muted'}>{label}</span>
      <span className={`font-medium tabular ${dark ? 'text-on-dark' : 'text-ink'}`}>{value}</span>
    </div>
  );
}

function Success({ member, result, onAnother }: { member: MemberSummary; result: SaleResult; onAnother: () => void }) {
  const navigate = useNavigate();
  const { membership, pt, partialError } = result;
  const recordedTotal = (membership ? Number(membership.amount) : 0) + (pt ? Number(pt.amount) : 0);
  const both = !!membership && !!pt;

  return (
    <section className="mx-auto max-w-md animate-pop-in py-8">
      <Card className="overflow-hidden p-0">
        <div className="flex flex-col items-center gap-3 bg-accent px-6 py-8 text-center text-accent-ink">
          <span className="inline-flex size-14 items-center justify-center rounded-full bg-accent-ink text-accent">
            <Check className="size-7" aria-hidden />
          </span>
          <div>
            <p className="font-display text-xl font-semibold">{both ? 'Sale recorded' : 'Payment recorded'}</p>
            <p className="text-sm text-accent-ink/70">
              {membership ? 'Gym access extended automatically.' : 'Package payment saved.'}
            </p>
          </div>
        </div>

        <div className="space-y-3 p-6">
          <Row label="Member" value={member.fullName} />

          {membership && (
            <>
              <Row label="Gym access" value={`${membership.planName ?? 'Plan'} · ${money(membership.amount)}`} />
              <div className="flex items-center justify-between">
                <span className="text-muted">Active until</span>
                <span className="rounded-pill bg-ok-soft px-2.5 py-1 font-medium text-ok-ink tabular">{formatDate(membership.periodEnd)}</span>
              </div>
            </>
          )}

          {pt && (
            <Row
              label="Package"
              value={`${pt.planName ?? 'Package'}${pt.coachName ? ` · ${pt.coachName}` : ''} · ${money(pt.amount)}`}
            />
          )}

          <div className="flex items-center justify-between border-t border-line pt-3">
            <span className="text-muted">Total collected</span>
            <span className="font-display text-lg font-semibold tabular text-ink">{money(recordedTotal)}</span>
          </div>

          {partialError && (
            <p className="flex items-start gap-2 rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{partialError} The membership above was saved.</span>
            </p>
          )}

          <p className="pt-1 text-center text-sm text-muted">A receipt is sent to the member automatically.</p>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button variant="outline" onClick={onAnother}>
              <Receipt className="size-4" aria-hidden />
              Record another
            </Button>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              Done
            </Button>
          </div>
        </div>
      </Card>
    </section>
  );
}
